import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Typography, message } from 'antd';

const FOCUS_MODE_STORAGE_KEY = 'reporting.focusMode';
const FOCUS_MODE_BODY_CLASS = 'report-focus-mode';
import { createTabChannel, getUserDetails, hasStudyViewingPermission, makePostCall } from '../../utils/helper';
import { postOnUnload } from '../../utils/unloadPost';
import { launchViewerOnDualMonitor } from '../../utils/MonitorManager';
import { getCentreProxyRs } from '../../utils/centreProxy';
import { usePageTitle } from '../../utils/pageTitle';
import type { OrderDetails } from './types';

// URL for steering the already-open viewer tab to `order`. The viewer reloads onto this
// URL, so it must carry the study's own centre proxy (&dicomProxy=) just like
// MonitorManager's launches — otherwise it silently falls back to the user's primary proxy.
const viewerSteerUrl = (order?: OrderDetails) => {
  const rs = getCentreProxyRs(order?.pacs_order?.po_center_code);
  return (
    `/viewer/viewer?StudyInstanceUIDs=${order?.ps_study_uid}` +
    (rs ? `&dicomProxy=${encodeURIComponent(rs)}` : '')
  );
};

import OOReportEditor from './components/Editor/OOReportEditor';

import './styles/reporting-page.css';

const { Text } = Typography;

/**
 * Top-level route component for `/oo-report`.
 *
 * Reads `order_ids` or `study_id` from search params, fetches order details,
 * and renders the study queue sidebar + report editor.
 */
const ReportingWorkspace: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ordersList, setOrdersList] = useState<OrderDetails[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetails | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [focusMode, setFocusMode] = useState<boolean>(() => {
    // Default ON — only off when localStorage explicitly says 'false'
    try { return localStorage.getItem(FOCUS_MODE_STORAGE_KEY) !== 'false'; }
    catch { return true; }
  });

  const toggleFocusMode = useCallback(() => {
    setFocusMode(prev => {
      const next = !prev;
      try { localStorage.setItem(FOCUS_MODE_STORAGE_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  // Apply body class so global header (rendered outside this route) can be hidden via CSS
  useEffect(() => {
    if (focusMode) document.body.classList.add(FOCUS_MODE_BODY_CLASS);
    else document.body.classList.remove(FOCUS_MODE_BODY_CLASS);
    return () => document.body.classList.remove(FOCUS_MODE_BODY_CLASS);
  }, [focusMode]);

  // Keyboard shortcut: Ctrl+Shift+F
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'F' || e.key === 'f')) {
        e.preventDefault();
        toggleFocusMode();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleFocusMode]);

  usePageTitle('Reporting', selectedOrder?.pacs_order?.patient?.pat_name);

  const order_ids = searchParams.get('order_ids');
  const study_id = searchParams.get('study_id');
  const channel = createTabChannel();

  // True once this reporting session has opened or steered a viewer tab. Only
  // then do we ask the viewer to close when the last study is finished — a
  // single-study session that never launched a viewer must not close some
  // unrelated viewer the user has open independently.
  const didDriveViewerRef = useRef(false);

  // The loaded queue lives in `ordersList` state, but the tab's URL is what a refresh
  // replays. Keep the two in step: when a study leaves the queue — unloaded by hand, or
  // finished and advanced past — drop its id from `order_ids` as well. Without this a
  // reload re-opens every study the user has already dealt with, since the backend only
  // filters out studies that are blocked or held by another user.
  //
  // Records what it wrote so the fetch effect below can tell our own rewrite apart from a
  // real navigation, and replaces rather than pushes so Back doesn't walk the user through
  // every intermediate queue state.
  const urlWrittenByUsRef = useRef<string | null>(null);

  const syncQueueToUrl = useCallback((remaining: OrderDetails[]) => {
    const next = new URLSearchParams(searchParams);
    if (!next.has('order_ids')) return; // study_id mode — no queue to keep in step
    const ids = remaining.map(o => o?.pacs_order?.pacs_ord_id).filter(Boolean).join(',');
    if (next.get('order_ids') === ids) return;
    next.set('order_ids', ids);
    urlWrittenByUsRef.current = ids;
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => () => channel.cleanup(), []);

  useEffect(() => {
    // Our own queue-sync rewrite of `order_ids` (syncQueueToUrl) must not re-trigger the
    // fetch — that would reload the whole queue from scratch and re-launch the viewer on
    // the first study, mid-session.
    if (order_ids !== null && order_ids === urlWrittenByUsRef.current) return;
    if (order_ids?.length) fetchOrderDetails(order_ids, null);
    else if (study_id) fetchOrderDetails('', study_id);
  }, [order_ids, study_id]);

  const fetchOrderDetails = async (orderIds: string, studyId: string | null) => {
    setLoading(true);
    try {
      const resp = await makePostCall('/orders-list-details', {
        order_ids: orderIds === '' ? [] : orderIds.split(','),
        study_uid: studyId,
        user_id: getUserDetails()?.username,
      });

      const ordData = resp?.data;

      // Studies the server refused to load: taken by another user, reporting blocked,
      // or beyond this user's concurrent-report limit. Show the first few individually
      // and summarise the rest — a large batch trimmed by the cap would otherwise bury
      // the screen in toasts.
      const skipped = [
        ...(ordData.conflictingStudies || []),
        ...(ordData.reportingDisabledStudies || []),
      ];
      const MAX_SKIP_TOASTS = 3;
      skipped.slice(0, MAX_SKIP_TOASTS).forEach(itm => {
        const reason = itm.opened_by ? `This study is being reported by ${itm.opened_by}.` : itm.reason || 'reporting disabled';
        message.warning(`${itm.study} - ${reason}`, 10);
      });
      if (skipped.length > MAX_SKIP_TOASTS) {
        message.warning(`${skipped.length - MAX_SKIP_TOASTS} more study(ies) were not loaded`, 10);
      }

      setOrdersList(ordData?.data || []);

      if (!(ordData?.data?.length > 0)) {
        message.info('No studies available for reporting');
        return;
      }

      handleOrderSelect(ordData.data[0], true, -1);
      // Typists get the editor only — they open the viewer on demand via the
      // editor's Launch Viewer button (which the in-queue steering below then follows).
      const disableViewer = searchParams.get('openviewer') === 'disable'
        || getUserDetails()?.user_type === 'typist';
      if (!disableViewer && !studyId && orderIds !== '' && hasStudyViewingPermission(getUserDetails())) {
        launchViewerOnDualMonitor(ordData.data[0]?.ps_study_uid, ordData.data[0]);
        didDriveViewerRef.current = true;
      }
    } catch {
      message.error('Failed to fetch order details');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderSelect = useCallback(async (order: OrderDetails, isInitial: boolean, index: number) => {
    const response = await makePostCall('/study-report-details', {
      user_id: getUserDetails()?.username,
      order_id: order?.pacs_order?.pacs_ord_id,
    });

    if (response?.data?.success) {
      const detail = response.data.data;
      setSelectedOrder({
        ...order,
        ow_diagnosed: detail?.ow_diagnosed ?? null,
        ow_correlated: detail?.ow_correlated ?? null,
      });
      setSelectedIndex(index);
    } else {
      if (response?.data?.limitReached) {
        const held = response.data.heldStudies || [];
        message.warning({
          content: (
            <div>
              <div>{response.data.message}</div>
              {held.length > 0 && (
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {held.map((s: string, i: number) => <li key={i}>{s}</li>)}
                </ul>
              )}
            </div>
          ),
          duration: 10,
        });
      } else if (response?.data?.patientLocked) {
        // Blocked by a lock on ANOTHER study of the same patient (the center is
        // configured for ORDER_GROUP / PATIENT_CENTER scope). Warning rather than
        // error, and held longer: nothing is broken, someone else simply has the
        // patient open, and the message names who and which accession.
        message.warning({ content: response.data.message, duration: 10 });
      } else {
        message.error(response?.data?.message);
      }
      // Keep the study the user was working on. A mid-queue refusal (limit reached,
      // or taken by someone else in the meantime) used to blank the whole right pane
      // and leave the session with nothing selected and no way back. On the initial
      // select there is no previous study to fall back to, so there we do clear.
      if (isInitial) {
        setSelectedOrder(null);
        setSelectedIndex(-1);
      }
      return;
    }

    if (isInitial) {
      // The initial select is made before the queue exists, with index -1 — the first study
      // is always at 0. (Its viewer is launched by fetchOrderDetails, not from here.)
      setSelectedIndex(0);
    } else if (hasStudyViewingPermission(getUserDetails())) {
      // Steer the already-open viewer tab to this study (in-place navigation).
      channel.sendUrlUpdate(viewerSteerUrl(order));
      didDriveViewerRef.current = true;
    }
    // A user without study-viewing permission (typist / consultant) has no viewer to steer,
    // and must KEEP the index they clicked. Forcing it to 0 here used to leave selectedOrder
    // and selectedIndex describing different studies, so signing off popped the wrong card
    // out of the queue and left the signed one in it.
  }, []);

  // Release the server-side reporting lock for a study that has just left the queue,
  // so its live-report "R" badge clears for everyone immediately.
  //
  // Sign-off already clears the lock inside its own status write (document-controller),
  // so only the other exits — draft, reviewed, delete — need this. Without it the study
  // stayed flagged as in-use until the 2-minute stale sweep (cleanupInactiveStudies),
  // which is the most common way users saw a stuck "R": drafting and moving on.
  //
  // Sent via postOnUnload (fetch keepalive) rather than axios because the single-study
  // branch below closes the window immediately afterwards, which cancels an in-flight XHR.
  const releaseReportLock = useCallback((order: OrderDetails | undefined, status: string) => {
    const orderId = order?.pacs_order?.pacs_ord_id;
    if (!orderId || status === 'SIGNEDOFF') return;
    postOnUnload('/close-report', { order_id: orderId, status: '' });
  }, []);

  // The editor holds the committed report on screen (read-only) for a few seconds before
  // calling this, so close/advance immediately here — an extra delay would only add blank
  // time after the editor has already torn itself down.
  const handlePostSaving = useCallback((status: string, _patDetails: OrderDetails) => {
    if (ordersList.length <= 1) {
      message.info('Closing the window..');
      releaseReportLock(ordersList[0], status);
      // Last study done — if this session drove a viewer, ask the viewer showing
      // this study to close too. The lone remaining order is what the viewer was
      // steered to, so pass its UID so only that viewer closes (not other viewer
      // tabs the user has open). Post the close before tearing down our own tab;
      // the BroadcastChannel message is already queued for delivery once
      // postMessage returns, so it survives this tab navigating away / closing.
      if (didDriveViewerRef.current && hasStudyViewingPermission(getUserDetails())) {
        channel.sendClose(ordersList[0]?.ps_study_uid);
      }
      window.open('about:blank', '_self');
      window.close();
      return;
    }

    releaseReportLock(ordersList[selectedIndex], status);

    const remaining = ordersList.filter((_, i) => i !== selectedIndex);
    const nextIndex = selectedIndex >= remaining.length ? remaining.length - 1 : selectedIndex;

    message.info('Moving to next study..');
    setOrdersList(remaining);
    syncQueueToUrl(remaining);
    setSelectedOrder(remaining[nextIndex]);
    setSelectedIndex(nextIndex);
    // Steer the already-open viewer tab to the next study after sign-off/close.
    if (hasStudyViewingPermission(getUserDetails())) {
      channel.sendUrlUpdate(viewerSteerUrl(remaining[nextIndex]));
    }
  }, [selectedIndex, ordersList, releaseReportLock, syncQueueToUrl]);

  // Manual unload of a study from the loaded queue (X button on the study card).
  const handleUnloadOrder = useCallback((order: OrderDetails, removeIdx: number) => {
    const remaining = ordersList.filter((_, i) => i !== removeIdx);
    if (remaining.length === 0) return; // guard; X is hidden when only one study is loaded

    // Release the server-side reporting lock so the study frees up (fire-and-forget).
    makePostCall('/close-report', { order_id: order?.pacs_order?.pacs_ord_id, status: '' })
      .catch(() => {});

    setOrdersList(remaining);
    syncQueueToUrl(remaining);

    if (removeIdx === selectedIndex) {
      // Unloaded the active study → auto-select the next remaining one.
      const nextIndex = removeIdx >= remaining.length ? remaining.length - 1 : removeIdx;
      handleOrderSelect(remaining[nextIndex], false, nextIndex);
    } else if (removeIdx < selectedIndex) {
      // Active study shifted up by one; keep it selected.
      setSelectedIndex(selectedIndex - 1);
    }
    // removeIdx > selectedIndex → active selection unchanged.
  }, [ordersList, selectedIndex, handleOrderSelect, syncQueueToUrl]);

  // Cancel abandons the reporting session and closes this window. A viewer this session
  // opened or steered belongs to that session, so it closes too — the same courtesy the
  // last sign-off already does in handlePostSaving, and for the same reason: leaving an
  // orphaned viewer behind means the radiologist closes two windows by hand every time.
  //
  // Which study the viewer is on: whichever is selected, since handleOrderSelect steers it
  // on every switch. Falling back to the first study covers the window between
  // fetchOrderDetails launching the viewer and a selection resolving.
  //
  // Guarded on didDriveViewerRef exactly as sign-off is, so a session that never touched a
  // viewer cannot close an unrelated one the user opened from the study list themselves.
  // Posted before tearing this window down: postMessage queues the message for delivery,
  // so it survives us navigating away.
  const handleCancel = useCallback(() => {
    if (didDriveViewerRef.current && hasStudyViewingPermission(getUserDetails())) {
      channel.sendClose((selectedOrder ?? ordersList[0])?.ps_study_uid);
    }
    window.open('about:blank', '_self');
    window.close();
  }, [selectedOrder, ordersList]);

  return (
    <div className={`reporting-page-layout${focusMode ? ' focus-mode' : ''}`}>
      <div className="content-layout">
        <div className="content-wrapper">
          {ordersList.length > 0 ? (
            <OOReportEditor
              patientDetails={selectedOrder}
              orders={ordersList}
              selectedIndex={selectedIndex}
              queueLoading={loading}
              onSelectOrder={(order, idx) => handleOrderSelect(order, false, idx)}
              onUnloadOrder={handleUnloadOrder}
              handlePostSaving={handlePostSaving}
              cancel={handleCancel}
              focusMode={focusMode}
              onToggleFocusMode={toggleFocusMode}
            />
          ) : (
            <div className="empty-state">
              <Text type="secondary">{loading ? 'Loading studies…' : 'No studies available for reporting'}</Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportingWorkspace;
